"use client";

import { Dialog, DialogContent } from "design-system/dialog";
import dynamic from "next/dynamic";
import { useCanCreateEmbeddedWallet } from "@/lib/hooks/flags/use-can-create-embedded-wallet";
import type { UseDialogReturn } from "@/lib/hooks/use-dialog";
import { LoadingTurnkeyView } from "@/subsystems/access/turnkey/components/views/loading-turnkey-view";
import type { SmsRecoveryDialogData } from "./sms-recovery-types";

// Both flows pull in the Turnkey SDK; load them only when the dialog first opens, like the auth dialog.
const SmsRecoverySetupFlow = dynamic(
  () => import("./sms-recovery-setup-flow").then((mod) => mod.SmsRecoverySetupFlow),
  { loading: () => <LoadingTurnkeyView />, ssr: false },
);
const SmsRecoveryDisableFlow = dynamic(
  () => import("./sms-recovery-disable-flow").then((mod) => mod.SmsRecoveryDisableFlow),
  { loading: () => <LoadingTurnkeyView />, ssr: false },
);

// One global dialog for every SMS-recovery action, opened from the three offer surfaces and the Profile section
// with the entry point and mode as payload.
export function SmsRecoveryDialog({ dialogControls }: { dialogControls: UseDialogReturn<SmsRecoveryDialogData> }) {
  const enabled = useCanCreateEmbeddedWallet();
  // Never unmount an open dialog on a transient flag read; see CreateEmbeddedWalletDialog.
  if (!enabled && !dialogControls.props.open) return null;
  const data = dialogControls.data;

  return (
    <Dialog {...dialogControls.props}>
      <DialogContent className="sm:max-w-xl">
        {data?.mode === "disable" ? (
          <SmsRecoveryDisableFlow entry={data.entry} onClose={dialogControls.dismiss} />
        ) : data ? (
          <SmsRecoverySetupFlow entry={data.entry} mode={data.mode} onClose={dialogControls.dismiss} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
